/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {RefObject, useCallback} from 'react';
import {TrackingFilterDrawerHandle} from '../../components/filter/current/types';

/**
 * Defines the inputs required to guard the current-theme Tracking filter
 * drawer's unapplied changes.
 */
interface UseTrackingFilterLeaveGuardParams {
  drawerOpen: boolean;
  drawerRef: RefObject<TrackingFilterDrawerHandle>;
  enabled: boolean;
}

// the drawer prompts an action can be run through ...
type GuardPrompt = 'confirmClose' | 'confirmLeave';

/**
 * Holds an action that would leave Tracking, or close the Tracking filter
 * drawer while staying on Tracking, until the user has decided what to do
 * with the drawer's unapplied changes. While enabled (current theme) and
 * the drawer is open, each action is handed to the drawer, which runs it at
 * once when there are no unapplied changes, otherwise once the user leaves
 * Tracking (`confirmLeave`) or applies or discards the changes
 * (`confirmClose`); otherwise - legacy theme, or the drawer closed - the
 * action runs at once.
 *
 * ```typescript
 *
 *   import useTrackingFilterLeaveGuard from '@/nav/current/useTrackingFilterLeaveGuard';
 *   ...
 *
 *   const {confirmClose, confirmLeave} = useTrackingFilterLeaveGuard({
 *     drawerOpen : filterDrawerOpen,
 *     drawerRef  : trackingFilterDrawerRef,
 *     enabled    : isCurrent
 *   });
 *
 *   // the reports page opens once the user leaves tracking ...
 *   confirmLeave(navigateToReports);
 *
 * ```
 *
 * @param   params Whether the guard is enabled, the drawer's open state, and
 *                 the drawer's handle.
 * @returns The callbacks that run an action through the drawer's Apply or
 *          Discard prompt (`confirmClose`) or its Leave Tracking prompt
 *          (`confirmLeave`).
 */
export const useTrackingFilterLeaveGuard = ({
  drawerOpen,
  drawerRef,
  enabled
}: UseTrackingFilterLeaveGuardParams) => {
  // hands the action to the drawer's prompt, or runs it straight away when
  // the guard is off or the drawer is closed ...
  const runThroughDrawer = useCallback(function(prompt: GuardPrompt, action: () => void) {
    const drawer = enabled && drawerOpen ? drawerRef.current : null;
    if (drawer) {
      drawer[prompt](action);
    } else {
      action();
    }
  }, [drawerOpen, drawerRef, enabled]);

  const confirmClose = useCallback(function(continueAction: () => void) {
    runThroughDrawer('confirmClose', continueAction);
  }, [runThroughDrawer]);

  const confirmLeave = useCallback(function(leave: () => void) {
    runThroughDrawer('confirmLeave', leave);
  }, [runThroughDrawer]);

  return {
    confirmClose,
    confirmLeave
  };
};

export default useTrackingFilterLeaveGuard;
